import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, ComposedChart, Legend } from 'recharts';
import { Download, Filter } from 'lucide-react';

const ProfitChart = ({ data: initialData, title, hideTitle = false, timePeriod, setTimePeriod, comparisonType, setComparisonType }) => {
  // Process data based on timePeriod and comparisonType
  const data = useMemo(() => {
    // Filter or transform data based on timePeriod
    let filteredData = [...initialData];
    
    // Apply time period filtering
    switch(timePeriod) {
      case 'daily':
        // For demo purposes, just take the most recent 7 data points
        filteredData = initialData.slice(-7);
        break;
      case 'weekly':
        // For demo purposes, take every other data point
        filteredData = initialData.filter((_, index) => index % 2 === 0);
        break;
      case 'quarterly':
        // For demo, take first month of each quarter
        filteredData = initialData.filter((_, index) => index % 3 === 0);
        break;
      case 'ytd':
        // For demo, take data from current year (assuming data is for current year)
        filteredData = initialData.slice(0);
        break;
      case 'monthly':
      default:
        // Use all data (monthly view is default)
        filteredData = initialData;
        break;
    }
    
    // Transform data based on comparisonType
    switch(comparisonType) {
      case 'previous':
        // Shift margin values to show previous period
        return filteredData.map((item, index, array) => ({
          ...item,
          previousMargin: index > 0 ? array[index-1].margin : null,
          comparisonLabel: 'Previous Period'
        }));
      case 'lastyear':
        // Simulate last year's data (for demo - 10% lower)
        return filteredData.map(item => ({
          ...item,
          previousMargin: item.margin * 0.9,
          comparisonLabel: 'Last Year'
        }));
      case 'target':
      default:
        // Use a target margin line (for demo - fixed at 0.85)
        return filteredData.map(item => ({
          ...item,
          targetMargin: 0.85,
          comparisonLabel: 'Target'
        }));
    }
  }, [initialData, timePeriod, comparisonType]);

  // Process data for different time periods
  const dailyData = data.slice(-7);
  const weeklyData = data.filter((_, index) => index % 2 === 0);
  const monthlyData = data.filter((_, index) => index % 3 === 0);
  const yearlyData = data.filter(item => new Date(item.date).getFullYear() === new Date().getFullYear());

  // Calculate profit margins
  const profitMargins = dailyData.map(item => ({
    date: item.date,
    margin: item.profit / item.revenue
  }));

  // Historical comparison data
  const lastYearData = dailyData.map(item => ({
    date: item.date,
    profit: item.profit * 0.9,
    revenue: item.revenue * 0.9
  }));

  // Target margin reference
  const targetMargin = 0.85;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        {!hideTitle && <h3 className="text-lg font-medium text-gray-900">{title}</h3>}
        
        {setTimePeriod && setComparisonType && (
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-2">
              <Filter size={14} className="text-gray-500" />
              <select 
                className="bg-white border border-gray-300 rounded-md px-2 py-1 text-xs"
                value={timePeriod}
                onChange={(e) => setTimePeriod(e.target.value)}
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="ytd">Year-to-Date</option>
              </select>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-500">Compare:</span>
              <select 
                className="bg-white border border-gray-300 rounded-md px-2 py-1 text-xs"
                value={comparisonType}
                onChange={(e) => setComparisonType(e.target.value)}
              >
                <option value="previous">Previous Period</option>
                <option value="lastyear">Same Period Last Year</option>
                <option value="target">Target</option>
              </select>
            </div>
            
            <button className="p-1 rounded-full hover:bg-gray-100">
              <Download size={16} className="text-gray-600" />
            </button>
          </div>
        )}
      </div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
            <XAxis dataKey="month" tick={{ fill: '#424242' }} />
            <YAxis domain={[0, 1]} tick={{ fill: '#424242' }} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#fff',
                border: '1px solid #E0E0E0',
                borderRadius: '4px'
              }} 
              formatter={(value, name) => {
                if (name === 'margin') return [value, 'Gross Profit'];
                if (name === 'previousMargin') return [value, data[0]?.comparisonLabel || 'Comparison'];
                if (name === 'targetMargin') return [value, 'Target Margin'];
                return [value, name];
              }}
            />
            <Legend />
            <Bar dataKey="margin" fill="#5E0060" name="Gross Profit" />
            
            {comparisonType === 'target' ? (
              <Line 
                type="monotone" 
                dataKey="targetMargin" 
                stroke="#FF6B6B" 
                strokeWidth={2} 
                strokeDasharray="5 5"
                dot={false}
                name="Target Margin"
              />
            ) : (
              <Line 
                type="monotone" 
                dataKey="previousMargin" 
                stroke="#FF6B6B" 
                strokeWidth={2} 
                strokeDasharray="5 5"
                dot={false}
                name={data[0]?.comparisonLabel || 'Comparison'}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {!hideTitle && (
        <div className="mt-4 flex justify-between items-center text-xs text-gray-500">
          <div>Profit margin trend</div>
          <div className="flex items-center gap-2">
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-purple-900 mr-1"></div>
              <span>Current</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-red-400 mr-1"></div>
              <span>{comparisonType === 'target' ? 'Target' : data[0]?.comparisonLabel || 'Comparison'}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfitChart;
